
// React hooks used for popup action callbacks, derived flags, refs, and local status state.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// API call used to save the complete focused queue result to the user's bucket.
import { saveAIGenerationQueueItemToBucket } from "api/APIStrichCalls";



const useQueueItemPopupActions = ({
  focusImage,
  setFocusImage,
  selectedWorkflow,
  getPrimaryWorkflowPromptField,
  getReusableTextPrompt,
  getQueueItemOutputDescription,
  setLastMainTextPrompt,
  setLastMainImageValue,
  handleWorkflowFieldChange,
  isWorkflowImageField,
  resolveWorkflowForFocusImage,
  getAIWorkFlow,
  imageInputWorkflows,
  getWorkflowImageInputSettingKey,
  getQueueItemIdentity,
  token,
  openCustomizr,
  toastSuccess,
  toastError,
  toastInfo,
}) => {

  // Save button/status state for the image shown in the popup.
  // We keep the target image id so status does not show on the wrong image.
  const [isSavingImageToBucket, setIsSavingImageToBucket] = useState(false);

  const [saveImageToBucketStatus, setSaveImageToBucketStatus] = useState("idle");

  const [saveImageToBucketTargetIdentity, setSaveImageToBucketTargetIdentity] = useState("");

  // Used to ignore old save responses when a newer save has started.
  const saveImageRequestIdRef = useRef(0);

  // Build a stable id for an image. Fallback to URL if needed.
  const getFocusIdentity = useCallback((image) => {

    // Guard against missing image to avoid errors.
    if (!image) return "";

    // Use provided identity function, but fallback to image URL or a stringified version of the image object if identity is missing.
    return getQueueItemIdentity(image) || String(image?.image_url || "");

  }, [getQueueItemIdentity]);



  // Quick check: does this value look like an image URL/data source?
  const isLikelyImageSource = useCallback((value) => {

    // Guard against non-string values to avoid errors.
    if (typeof value !== "string") return false;

    // Basic check for common URL patterns and image file extensions.
    const normalized = value.trim().toLowerCase();

    if (!normalized) return false;

    return (

      // IS IT A URL? (starts with http/https/data:image)
      normalized.startsWith("http://") ||
      normalized.startsWith("https://") ||
      normalized.startsWith("data:image/") ||

      // OR DOES IT LOOK LIKE AN IMAGE FILE? (ends with common image extensions, allowing for query params)
      /\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/.test(normalized)
    );

  }, []);

  // True when settings contain a non-empty reusable prompt.
  const hasReusableTextPrompt = useCallback((settings) => {

    // Check if there is a reusable prompt available in the settings using the provided function.
    return Boolean(getReusableTextPrompt(settings));

  }, [getReusableTextPrompt]);



  // Reuse prompt text from a queue item.
  // If the current workflow cannot use it, save it for later.
  const handleUsePrompt = useCallback((settings) => {

    // Look at editable fields of the currently selected workflow.
    const selectedEditableSettings = (selectedWorkflow?.settings || [])
      .filter((field) => field?.user_editable === "Y" || field?.user_editable === true);

    // Find the main prompt field for this workflow.
    const selectedPromptField = getPrimaryWorkflowPromptField(selectedEditableSettings);

    // Try workflow prompt field first, then common prompt fields.
    const sourcePrompt = settings?.[selectedPromptField?.setting]
      ?? getReusableTextPrompt(settings)
      ?? settings?.positive_prompt
      ?? settings?.prompt
      ?? "";

    // Basic check to avoid saving empty/invalid prompts.
    if (!sourcePrompt || sourcePrompt.trim().length === 0) {

      // Show the toast error 
      toastError({ title: "Error", message: "No reusable prompt found on this item." });

      return;

    }

    // Remember this prompt even if we cannot apply it right now.
    setLastMainTextPrompt(sourcePrompt);

    // Apply immediately when current workflow supports prompt input.
    let appliedToCurrentWorkflow = false;

    // Only apply to editable fields of the current workflow.
    if (selectedWorkflow && selectedPromptField?.setting) {

      // Apply prompt to the workflow form.
      handleWorkflowFieldChange(selectedPromptField.setting, sourcePrompt);
      
      // Mark that we applied the prompt to the current workflow so we can show the right message.
      appliedToCurrentWorkflow = true;

    }

    // Message depends on whether prompt was applied now or only remembered for later.
    if (appliedToCurrentWorkflow) {

      // Prompt was applied to current workflow: show confirmation.
      toastInfo({ title: "Information", message: "Prompt reused in current workflow form." });
    
    } else {

      // Prompt was saved for later: show information.
      toastInfo({ title: "Information", message: "Prompt saved. It will be applied when a workflow with prompt input is selected." });
 
    }

    // Close popup after action is complete.
    setFocusImage(null);

  }, [
    selectedWorkflow,
    getPrimaryWorkflowPromptField,
    getReusableTextPrompt,
    toastError,
    setLastMainTextPrompt,
    handleWorkflowFieldChange,
    toastInfo,
    setFocusImage,
  ]);

  // Reuse output description text from a queue item.
  // Apply now if selected workflow has prompt input, otherwise remember for later.
  const handleUseOutputDescription = useCallback((img) => {

    // Extract description text from this queue item output payload.
    const description = getQueueItemOutputDescription(img);

    // If there is no description, stop and show an error.
    if (!description) {

      // show the Toast error
      toastError({ title: "Error", message: "No output description found on this queue item." });

      return;
    
    }

    // Always remember description so it can be reused after workflow changes.
    setLastMainTextPrompt(description);

    // Track whether description was applied to current workflow form right now.
    let appliedToCurrentWorkflow = false;

    // Only try to apply now when there is an active selected workflow.
    if (selectedWorkflow) {

      // Only editable settings can be updated from popup reuse actions.
      const editableSettings = (selectedWorkflow.settings || [])
        .filter((field) => field?.user_editable === "Y" || field?.user_editable === true);

      // Find the best prompt field for this workflow.
      const promptField = getPrimaryWorkflowPromptField(editableSettings);

      // Apply description if workflow has a valid prompt setting key.
      if (promptField?.setting) {

        handleWorkflowFieldChange(promptField.setting, description);

        appliedToCurrentWorkflow = true;

      }

    }

    // Show message based on whether we applied now or only saved for later.
    if (appliedToCurrentWorkflow) {

      // Description was applied to current workflow: show confirmation.
      toastInfo({ title: "Information", message: "Description reused in current workflow form." });
 
    } else {

      // Description was saved for later: show information.
      toastInfo({ title: "Information", message: "Description saved. It will be applied when a workflow with prompt input is selected." });
 
    }

    // Close popup after action is complete.
    setFocusImage(null);

  }, [
    getPrimaryWorkflowPromptField,
    getQueueItemOutputDescription,
    handleWorkflowFieldChange,
    selectedWorkflow,
    setLastMainTextPrompt,
    setFocusImage,
    toastError,
    toastInfo,
  ]);

  // Reuse output image in the current workflow, or save it for later.
  const handleUseImage = useCallback((imageUrl) => {

    // Guard against empty image input.
    if (!imageUrl) {

      // Basic check to avoid saving invalid image URLs.
      toastError({ title: "Error", message: "No image URL provided." });

      return;

    }

    // Remember image value for workflow switches.
    setLastMainImageValue(imageUrl);


    // Track whether we applied the image to the current workflow form
    let appliedToCurrentWorkflow = false;

    // Do we have a selected workflow to apply to right now?
    if (selectedWorkflow) {

      // Only editable settings can be set from popup actions.
      const editableSettings = (selectedWorkflow.settings || []).filter((field) => field?.user_editable === "Y" || field?.user_editable === true);

      // If there are multiple image fields, use the first one.
      const imageField = editableSettings.find((field) => isWorkflowImageField(field));
      
      // If we found an image field, apply the image URL to it.
      if (imageField?.setting) {

        // Apply image to the workflow form.
        handleWorkflowFieldChange(imageField.setting, imageUrl);
     
        // Mark that we applied the image to the current workflow so we can show the right message.
        appliedToCurrentWorkflow = true;

      }

    }

    // Message depends on whether image was applied now or only remembered.
    if (appliedToCurrentWorkflow) {

      // Image was applied to current workflow: show confirmation.
      toastInfo({ title: "Information", message: "Image reused in current workflow form." });

    } else {

      // Image was saved for later: show information.
      toastInfo({ title: "Information", message: "Image saved. It will be applied when a workflow with image input is selected." });

    }

    // Close popup after action is complete.
    setFocusImage(null);

  }, [
    toastError,
    setLastMainImageValue,
    selectedWorkflow,
    isWorkflowImageField,
    handleWorkflowFieldChange,
    toastInfo,
    setFocusImage,
  ]);

    // Save the complete queue result and all associated files to the user's bucket.
  // Includes basic protection against duplicate/old responses.
  const handleSaveImageToBucket = useCallback(async (image) => {

    // Do not start a second save while one is already running.
    if (isSavingImageToBucket) return;

    // A queue job is required because the API resolves the definitive file and associated children.
    if (!image?.job) {

      // Show an error if this queue item cannot be identified.
      toastError({ title: "Error", message: "No queue job was available to save." });

      return;

    }

    // Store target id so status is tied to the right queue item.
    const saveTargetIdentity = getQueueItemIdentity(image) || String(image.job);

    // Increase request id to mark this as newest save request.
    const requestId = saveImageRequestIdRef.current + 1;

    // Save the current request id in the ref so we can ignore old responses.
    saveImageRequestIdRef.current = requestId;

    // Show saving UI for this queue item.
    setSaveImageToBucketTargetIdentity(saveTargetIdentity);

    // Set saving state to true to disable the button and show loading status.
    setIsSavingImageToBucket(true);

    // Keep this as "saving" until API returns success/failure.
    setSaveImageToBucketStatus("saving");

    try {

      // Ask the API to copy the definitive AI bucket item and all associated files into the user's bucket.
      const response = await saveAIGenerationQueueItemToBucket(token, image.job);

      // Ignore this result if a newer save started after it.
      if (requestId !== saveImageRequestIdRef.current) return;

      if (response?.success) {

        // Keep "saved" status visible until focus changes or popup closes.
        toastSuccess({ title: "Success", message: "Saved to bucket." });

        // Update status to saved to show success.
        setSaveImageToBucketStatus("saved");

        // Allow another copy to be saved if the user deliberately clicks again.
        setIsSavingImageToBucket(false);

        return;

      }

      // Start with a generic error if the API did not supply one.
      let errorMessage = "Unknown error";

      // Prefer the API error when one was returned.
      if (response?.error_message) { errorMessage = response.error_message; }

      // Reset status on failure.
      toastError({ title: "Error", message: "Failed to save to bucket: " + errorMessage });

      // Reset saving state to allow retrying.
      setIsSavingImageToBucket(false);

      // Reset status to idle so UI goes back to normal state.
      setSaveImageToBucketStatus("idle");

      // Clear target identity so status does not show on the wrong queue item.
      setSaveImageToBucketTargetIdentity("");

    } catch (error) {

      // Also ignore stale errors from old requests.
      if (requestId !== saveImageRequestIdRef.current) return;

      console.error("Failed to save queue result to bucket:", error);

      // Reset status after unexpected error.
      toastError({ title: "Error", message: "Something went wrong while saving to the bucket. Please try again." });

      // Reset saving state to allow retrying.
      setIsSavingImageToBucket(false);

      // Reset status to idle so UI goes back to normal state.
      setSaveImageToBucketStatus("idle");

      // Clear target identity so status does not show on the wrong queue item.
      setSaveImageToBucketTargetIdentity("");

    }

  }, [
    isSavingImageToBucket,
    toastError,
    getQueueItemIdentity,
    token,
    toastSuccess,
  ]);



  // Reset save status when popup closes or user moves to another image.
  useEffect(() => {

    const focusIdentity = getFocusIdentity(focusImage);

    // Popup closed: clear status.
    if (!focusIdentity) {

      // Clear save status and target when popup closes to avoid showing stale status on the wrong image later.
      setIsSavingImageToBucket(false);

      setSaveImageToBucketStatus("idle");

      setSaveImageToBucketTargetIdentity("");

      return;

    }

    // User moved to a different image: clear previous image status.
    if (

      // If there is an active save status for a different image, clear it since we are now focused on another image.
      saveImageToBucketStatus !== "idle" &&
      saveImageToBucketTargetIdentity &&
      focusIdentity !== saveImageToBucketTargetIdentity

    ) {

      // Clear save status and target when user moves to a different image to avoid showing status on the wrong image.
      setIsSavingImageToBucket(false);

      setSaveImageToBucketStatus("idle");

      setSaveImageToBucketTargetIdentity("");

    }
    
  }, [focusImage, getFocusIdentity, saveImageToBucketStatus, saveImageToBucketTargetIdentity]);


  // Open the image in Customizr.
  const handleOpenCustomizrClick = useCallback(async (tokenType, image_data, saveToImageBucket = "Y", returnImage = false, setLoadingCustomizrEdit = null) => {

    try {

      // Build the Customizr configuration
      const customizrConfig = {
        type: tokenType,
        imageData: image_data,
        save_image_to_bucket: saveToImageBucket,
        return_image: returnImage,
      };

      // Open Customizr and wait for the returned result
      const data = await openCustomizr(customizrConfig);

      // If Customizr closes without returning an image, stop without changing anything
      if (!data?.results?.image_base64) {

        // Show a useful error when this workflow specifically requested the edited image
        if (returnImage && typeof toastError === "function") {

          toastError({
            title: "Error",
            message: "Customizr closed without returning the edited image.",
          });

        }

        return null;

      }

      // Get the returned image
      const imageBase64 = data.results.image_base64;

      // If the caller wants the image returned, return it.
      if (returnImage) { return imageBase64; }

      // Otherwise redirect the user to the image bucket page.
      window.location.href = "/image-bucket";

      // Return the image in case the caller wants to inspect the completed result.
      return imageBase64;

    } catch (error) {

      // Log the error for debugging purposes.
      console.error("Customizr did not complete:", error);

      // Display a user-friendly error message if a toast handler has been supplied.
      if (typeof toastError === "function") {
        toastError({
          title: "Error",
          message: error?.message || "Something went wrong while opening the image editor. Please try again.",
        });
      }

      // Return null so callers can safely treat this as no image returned.
      return null;

    } finally {

      // If a loading state function has been passed in, always clear it when Customizr finishes.
      if (setLoadingCustomizrEdit) {

        // Clear the loading state.
        setLoadingCustomizrEdit(false);

      }

    }

  }, [openCustomizr, toastError]);




  // Load this image's settings back into a matching workflow form.
  // Then close popup and scroll to the form.
  const handleReuseFocusImageSettings = useCallback(() => {

    // Need a selected popup image first.
    if (!focusImage) {

      // Basic check to avoid errors and show message if user tries to reuse settings with no image in focus, which should not normally happen since the button is only shown when there is a focus image, but this adds extra safety.
      toastError({ title: "Error", message: "No focus image selected." });

      return;

    }

    // Find best matching workflow for this image/settings.
    const workflow = resolveWorkflowForFocusImage(focusImage);

    if (!workflow) {

      toastError({ title: "Error", message: "Could not find matching workflow for this image." });

      return;

    }

    // Load image settings into matched workflow form.
    getAIWorkFlow(workflow, focusImage.settings || {});

    setFocusImage(null);

    // Move user to the workflow form after applying settings.
    const workflowFormElement = document.getElementById("ai-workflow-form");

    workflowFormElement?.scrollIntoView({ behavior: "smooth", block: "start" });

  }, [focusImage, toastError, resolveWorkflowForFocusImage, getAIWorkFlow, setFocusImage]);



  // Start a selected image workflow using the current focus image as input.
  const handleRunFocusImageWorkflow = useCallback((workflowCode) => {

    // Need an image output to feed into image-based workflows.
    if (!focusImage?.image_url) {

      toastError({ title: "Error", message: "No image URL provided." });

      return;

    }

    // Find requested workflow in image-capable workflow list.
    const workflow = imageInputWorkflows.find((item) => item.code === workflowCode);

    if (!workflow) {

      toastError({ title: "Error", message: "Workflow not found." });

      return;

    }

    // Find which setting key accepts the image input.
    const imageSettingKey = getWorkflowImageInputSettingKey(workflow);

    if (!imageSettingKey) {

      toastError({ title: "Error", message: "Selected workflow does not accept image input." });

      return;

    }

    // Open workflow form prefilled with focus image URL.
    getAIWorkFlow(workflow, { [imageSettingKey]: focusImage.image_url });

    setFocusImage(null);

    // Scroll to form so user can review or run immediately.
    const workflowFormElement = document.getElementById("ai-workflow-form");

    workflowFormElement?.scrollIntoView({ behavior: "smooth", block: "start" });

  }, [
    focusImage,
    toastError,
    imageInputWorkflows,
    getWorkflowImageInputSettingKey,
    getAIWorkFlow,
    setFocusImage,
  ]);

  // Show save status only for the image currently in focus.
  const showSaveStatusForCurrentFocus = useMemo(() => {

    const focusIdentity = getFocusIdentity(focusImage);

    // Show only when status is active and belongs to current focus image.
    return (
      saveImageToBucketStatus !== "idle" &&
      focusIdentity.length > 0 &&
      focusIdentity === saveImageToBucketTargetIdentity
    );

  }, [focusImage, getFocusIdentity, saveImageToBucketStatus, saveImageToBucketTargetIdentity]);



  // True only when the focused image is currently saving.
  const isSavingCurrentFocusImage = useMemo(() => {

    const focusIdentity = getFocusIdentity(focusImage);

    // True only while current focus image is actively saving.
    return (
      isSavingImageToBucket &&
      focusIdentity.length > 0 &&
      focusIdentity === saveImageToBucketTargetIdentity
    );

  }, [focusImage, getFocusIdentity, isSavingImageToBucket, saveImageToBucketTargetIdentity]);

  // Return popup action helpers and status values grouped by usage area.
  return {

    // Reuse helpers for prompt/image/description actions.
    isLikelyImageSource,
    hasReusableTextPrompt,
    handleUseOutputDescription,
    handleUsePrompt,
    handleUseImage,

    // Save/edit/re-run actions.
    handleSaveImageToBucket,
    handleOpenCustomizrClick,
    handleReuseFocusImageSettings,
    handleRunFocusImageWorkflow,

    // Save status flags for current focus image.
    showSaveStatusForCurrentFocus,
    isSavingCurrentFocusImage,
    isSavingImageToBucket,
    saveImageToBucketStatus,

  };

};

export default useQueueItemPopupActions;
